import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CoolifyServerDto,
  PublishedMigrationContent,
  MigrationBuildPath,
  MigrationCheckState,
  MigrationDatabaseDto,
  MigrationOverviewDto,
  MigrationSettingsDto,
  MigrationSiteDto,
  MigrationWorkflowDetailDto,
  MigrationWorkflowDto,
  MigrationWorkflowState,
} from '@cpm/shared';
import { MIGRATION_WORKFLOW_STATES, publishedMigrationContentSchema, cutoverMigrationSiteSchema } from '@cpm/shared';
import { ArrowRight, RefreshCw, Undo2 } from 'lucide-react';
import { useState } from 'react';
import { api } from '../api/client.js';
import { useMe } from '../api/auth.js';
import { timeAgo } from '../components/charts.js';
import { HelpLink } from '../components/help.js';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorNote,
  Input,
  Label,
  PageHeader,
  Select,
  Spinner,
  Textarea,
  type BadgeTone,
} from '../components/ui.js';

/**
 * G14 workload migration dashboard: the source connection (admin), the
 * per-subscription workflow list with states, and a detail view driving
 * the guided steps (provision → transfer → verify → cutover) with the
 * acceptance checklist and DNS rollback. See docs/migration-runbook.md.
 */

const STATE_LABELS: Record<MigrationWorkflowState, string> = {
  inventoried: 'Inventoried',
  planned: 'Planned',
  content_moved: 'Content moved',
  verified: 'Verified',
  cutover: 'Cut over',
  done: 'Done',
};

const STATE_TONES: Record<MigrationWorkflowState, BadgeTone> = {
  inventoried: 'gray',
  planned: 'blue',
  content_moved: 'blue',
  verified: 'yellow',
  cutover: 'yellow',
  done: 'green',
};

const BUILD_PATH_LABELS: Record<MigrationBuildPath, string> = {
  static: 'Static (nginx)',
  php: 'PHP (Dockerfile)',
  nixpacks: 'Nixpacks (git)',
  forwarding: 'Forwarding',
  none: 'No hosting',
};

function siteTone(state: string): BadgeTone {
  if (state === 'done' || state === 'verified') return 'green';
  if (state === 'skipped') return 'gray';
  if (state === 'cutover' || state === 'content_moved') return 'yellow';
  if (state === 'provisioned') return 'blue';
  return 'gray';
}

function checkTone(state: MigrationCheckState): BadgeTone {
  return state === 'passed' ? 'green' : state === 'failed' ? 'red' : state === 'skipped' ? 'gray' : 'yellow';
}

export function MigrationPage() {
  const me = useMe();
  const isAdmin = me.data?.role === 'admin';
  const [selected, setSelected] = useState<string | null>(null);

  const overview = useQuery({
    queryKey: ['migration', 'overview'],
    queryFn: () => api.get<MigrationOverviewDto>('/api/migration/'),
    refetchInterval: (q) =>
      (q.state.data?.workflows ?? []).some((w) => w.state !== 'done') ? 8000 : false,
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Migration"
        description="Guided per-site workload migration from Plesk onto Coolify. Plesk keeps serving until each site is verified; rollback is a DNS revert."
      />

      {isAdmin && <SettingsCard />}

      {overview.isPending ? (
        <Spinner label="Loading migrations…" />
      ) : overview.error ? (
        <ErrorNote error={overview.error} />
      ) : selected ? (
        <WorkflowDetail
          workflowId={selected}
          onBack={() => {
            setSelected(null);
            void overview.refetch();
          }}
        />
      ) : (
        <>
          {!overview.data!.configured && (
            <Card>
              <div className="px-5 py-4 text-sm text-amber-700 dark:text-amber-400">
                Configure the migration source connection{isAdmin ? ' above' : ' (admin)'} before starting a
                migration.
              </div>
            </Card>
          )}
          <WorkflowList overview={overview.data!} onOpen={setSelected} />
        </>
      )}
    </div>
  );
}

// ── Source connection settings (admin) ──────────────────────────────

function SettingsCard() {
  const qc = useQueryClient();
  const settings = useQuery({
    queryKey: ['migration', 'settings'],
    queryFn: () => api.get<MigrationSettingsDto>('/api/migration/settings'),
  });
  const [form, setForm] = useState<{ sshHost: string; sshPort: number; sshUser: string; privateKey: string }>({
    sshHost: '',
    sshPort: 22,
    sshUser: 'root',
    privateKey: '',
  });
  const [open, setOpen] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      api.put('/api/migration/settings', {
        sshHost: form.sshHost || settings.data?.sshHost,
        sshPort: form.sshPort,
        sshUser: form.sshUser,
        ...(form.privateKey ? { privateKey: form.privateKey } : {}),
      }),
    onSuccess: () => {
      setForm((f) => ({ ...f, privateKey: '' }));
      void qc.invalidateQueries({ queryKey: ['migration'] });
      setOpen(false);
    },
  });

  const test = useMutation({
    mutationFn: () => api.post<{ ok: boolean; error: string | null }>('/api/migration/test'),
  });

  const s = settings.data;
  return (
    <Card>
      <CardHeader
        title="Source connection"
        description="The licensed Plesk box the workloads move off. Reads are done directly; bulk transfers pull from here onto the Coolify server."
        actions={
          <Button variant="secondary" onClick={() => setOpen((v) => !v)}>
            {open ? 'Close' : s?.hasPrivateKey ? 'Edit' : 'Configure'}
          </Button>
        }
      />
      <div className="space-y-3 px-5 py-4 text-sm">
        {s && !open && (
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-slate-600 dark:text-slate-300">
            <span>
              Host: <strong>{s.sshHost || '—'}</strong>
            </span>
            <span>Port: {s.sshPort}</span>
            <span>User: {s.sshUser}</span>
            <Badge tone={s.hasPrivateKey ? 'green' : 'yellow'}>
              {s.hasPrivateKey ? 'SSH key set' : 'no SSH key'}
            </Badge>
            <Button
              variant="ghost"
              onClick={() => test.mutate()}
              loading={test.isPending}
              disabled={!s.hasPrivateKey}
            >
              Test connection
            </Button>
            {test.data && (
              <Badge tone={test.data.ok ? 'green' : 'red'}>
                {test.data.ok ? 'reachable' : (test.data.error ?? 'failed')}
              </Badge>
            )}
          </div>
        )}
        {open && (
          <form
            className="grid gap-3 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate();
            }}
          >
            <div>
              <Label htmlFor="mig-host">SSH host</Label>
              <Input
                id="mig-host"
                defaultValue={s?.sshHost}
                placeholder="plesk.example.com"
                onChange={(e) => setForm((f) => ({ ...f, sshHost: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="mig-port">Port</Label>
                <Input
                  id="mig-port"
                  type="number"
                  defaultValue={s?.sshPort ?? 22}
                  onChange={(e) => setForm((f) => ({ ...f, sshPort: Number(e.target.value) }))}
                />
              </div>
              <div>
                <Label htmlFor="mig-user">User</Label>
                <Input
                  id="mig-user"
                  defaultValue={s?.sshUser ?? 'root'}
                  onChange={(e) => setForm((f) => ({ ...f, sshUser: e.target.value }))}
                />
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="mig-key">SSH private key {s?.hasPrivateKey && '(leave blank to keep current)'}</Label>
              <Textarea
                id="mig-key"
                rows={4}
                className="font-mono text-xs"
                placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
                onChange={(e) => setForm((f) => ({ ...f, privateKey: e.target.value }))}
              />
              <p className="mt-1 text-xs text-slate-500">
                Root key on the Plesk box (dumps and content transfer need read access to docroots and databases).
              </p>
            </div>
            <div className="sm:col-span-2">
              {save.error && <ErrorNote error={save.error} />}
              <Button type="submit" loading={save.isPending}>
                Save connection
              </Button>
            </div>
          </form>
        )}
      </div>
    </Card>
  );
}

// ── Workflow list + eligible subscriptions ──────────────────────────

function WorkflowList({
  overview,
  onOpen,
}: {
  overview: MigrationOverviewDto;
  onOpen: (id: string) => void;
}) {
  const qc = useQueryClient();
  const start = useMutation({
    mutationFn: (subscriptionId: string) =>
      api.post<MigrationWorkflowDetailDto>('/api/migration/workflows', { subscriptionId }),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['migration', 'overview'] });
      onOpen(res.workflow.id);
    },
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader title="Migrations" description="One workflow per subscription." />
        {overview.workflows.length === 0 ? (
          <EmptyState title="No migrations yet" description="Start one from an eligible subscription below." />
        ) : (
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            {overview.workflows.map((w) => (
              <li key={w.id}>
                <button
                  className="flex w-full items-center justify-between gap-3 px-5 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  onClick={() => onOpen(w.id)}
                >
                  <div className="min-w-0">
                    <div className="truncate font-medium">{w.subscriptionName}</div>
                    <div className="truncate text-xs text-slate-500">
                      {w.clientName ?? '—'} · updated {timeAgo(w.updatedAt)}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <WorkflowProgress state={w.state} />
                    <Badge tone={STATE_TONES[w.state]}>{STATE_LABELS[w.state]}</Badge>
                    <ArrowRight className="size-4 text-slate-400" aria-hidden />
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {overview.eligible.length > 0 && (
        <Card>
          <CardHeader
            title="Eligible subscriptions"
            description="Imported from Plesk with no migration workflow yet."
          />
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            {overview.eligible.map((e) => (
              <li key={e.subscriptionId} className="flex items-center justify-between gap-3 px-5 py-3">
                <div className="min-w-0">
                  <div className="truncate font-medium">{e.subscriptionName}</div>
                  <div className="truncate text-xs text-slate-500">
                    {e.clientName ?? '—'}
                    {e.pleskName && ` · Plesk: ${e.pleskName}`}
                  </div>
                </div>
                <Button
                  variant="secondary"
                  loading={start.isPending && start.variables === e.subscriptionId}
                  onClick={() => start.mutate(e.subscriptionId)}
                >
                  Start migration
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      )}
      {start.error && <ErrorNote error={start.error} />}
    </div>
  );
}

/** Compact dots showing where the workflow is along the pipeline. */
function WorkflowProgress({ state }: { state: MigrationWorkflowState }) {
  const idx = MIGRATION_WORKFLOW_STATES.indexOf(state);
  return (
    <div className="hidden items-center gap-1 sm:flex" aria-hidden>
      {MIGRATION_WORKFLOW_STATES.map((s, i) => (
        <span
          key={s}
          className={
            i <= idx
              ? 'size-1.5 rounded-full bg-brand-500'
              : 'size-1.5 rounded-full bg-slate-200 dark:bg-slate-700'
          }
        />
      ))}
    </div>
  );
}

// ── Workflow detail ─────────────────────────────────────────────────

function WorkflowDetail({ workflowId, onBack }: { workflowId: string; onBack: () => void }) {
  const qc = useQueryClient();
  const detail = useQuery({
    queryKey: ['migration', 'workflow', workflowId],
    queryFn: () => api.get<MigrationWorkflowDetailDto>(`/api/migration/workflows/${workflowId}`),
    refetchInterval: (q) => (q.state.data && q.state.data.workflow.state !== 'done' ? 6000 : false),
  });
  const servers = useQuery({
    queryKey: ['coolify', 'servers'],
    queryFn: () => api.get<CoolifyServerDto[]>('/api/coolify/servers'),
  });
  const [serverUuid, setServerUuid] = useState<string>('');

  const invalidate = () => qc.invalidateQueries({ queryKey: ['migration', 'workflow', workflowId] });

  const generate = useMutation({
    mutationFn: (probe: boolean) =>
      api.post(`/api/migration/workflows/${workflowId}/plan`, { probe }),
    onSuccess: invalidate,
  });
  const transition = useMutation({
    mutationFn: (to: MigrationWorkflowState) =>
      api.post(`/api/migration/workflows/${workflowId}/transition`, { to }),
    onSuccess: invalidate,
  });

  if (detail.isPending) return <Spinner label="Loading workflow…" />;
  if (detail.error) return <ErrorNote error={detail.error} />;
  const d = detail.data!;
  const effectiveServer = serverUuid || servers.data?.[0]?.uuid || '';

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <button className="text-sm text-brand-600 hover:underline" onClick={onBack}>
          ← All migrations
        </button>
        <Badge tone={STATE_TONES[d.workflow.state]}>{STATE_LABELS[d.workflow.state]}</Badge>
      </div>

      <Card>
        <CardHeader
          title={d.workflow.subscriptionName}
          description={`${d.workflow.clientName ?? '—'} · ${d.mail.mailboxes} mailbox(es)`}
          actions={
            <div className="flex flex-wrap items-center gap-2">
              {d.workflow.plan && (
                <Button
                  variant="ghost"
                  loading={generate.isPending}
                  onClick={() => generate.mutate(true)}
                  disabled={!['inventoried', 'planned'].includes(d.workflow.state)}
                >
                  <RefreshCw className="size-4" /> Re-plan
                </Button>
              )}
              {d.eligibleTransitions.map((to) => (
                <Button key={to} loading={transition.isPending} onClick={() => transition.mutate(to)}>
                  Advance to {STATE_LABELS[to]}
                </Button>
              ))}
            </div>
          }
        />
        <div className="space-y-2 px-5 py-4 text-sm">
          {!d.workflow.plan && (
            <div className="flex items-center gap-3">
              <p className="text-slate-600 dark:text-slate-300">
                Generate a per-site migration plan from the Plesk inventory.
              </p>
              <Button loading={generate.isPending} onClick={() => generate.mutate(true)}>
                Generate plan (with docroot probe)
              </Button>
              <Button variant="ghost" loading={generate.isPending} onClick={() => generate.mutate(false)}>
                Without probe
              </Button>
            </div>
          )}
          {generate.error && <ErrorNote error={generate.error} />}
          {transition.error && <ErrorNote error={transition.error} />}
          {d.blockedTransitions.length > 0 && (
            <ul className="text-xs text-slate-500">
              {d.blockedTransitions.map((b) => (
                <li key={b.to}>
                  <span className="font-medium">→ {STATE_LABELS[b.to]}:</span> {b.reason}
                </li>
              ))}
            </ul>
          )}
          {d.workflow.plan && d.workflow.plan.warnings.length > 0 && (
            <ul className="list-disc space-y-0.5 pl-5 text-xs text-amber-700 dark:text-amber-400">
              {d.workflow.plan.warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          )}
        </div>
      </Card>

      {d.workflow.plan && (
        <Card>
          <CardHeader
            title="Target server"
            description="Where the Coolify applications and databases are provisioned."
          />
          <div className="px-5 py-4">
            {servers.isPending ? (
              <Spinner label="Loading servers…" />
            ) : servers.error ? (
              <ErrorNote error={servers.error} />
            ) : (
              <Select
                value={effectiveServer}
                onChange={(e) => setServerUuid(e.target.value)}
                className="max-w-sm"
              >
                {servers.data!.map((srv) => (
                  <option key={srv.uuid} value={srv.uuid}>
                    {srv.name} ({srv.ip})
                  </option>
                ))}
              </Select>
            )}
          </div>
        </Card>
      )}

      {d.workflow.plan && (
        <Card>
          <CardHeader
            title="Preserved domain topology"
            description={`${d.workflow.plan.topology.length} source name(s), including names that remain DNS/mail-only.`}
          />
          <div className="overflow-x-auto px-5 py-4">
            <table className="w-full text-left text-xs">
              <thead className="text-slate-400">
                <tr>
                  <th className="py-1 pr-4 font-medium">Source name</th>
                  <th className="py-1 pr-4 font-medium">Role</th>
                  <th className="py-1 pr-4 font-medium">Destination</th>
                  <th className="py-1 font-medium">Owner</th>
                </tr>
              </thead>
              <tbody>
                {d.workflow.plan.topology.map((row) => (
                  <tr key={`${row.kind}:${row.name}`} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="py-1.5 pr-4 font-mono">{row.name}</td>
                    <td className="py-1.5 pr-4">{row.kind}</td>
                    <td className="py-1.5 pr-4">{row.destination.replaceAll('-', ' ')}</td>
                    <td className="py-1.5 font-mono">{row.applicationDomain ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {d.sites.map((site) => (
        <SiteCard
          key={site.id}
          workflowId={workflowId}
          site={site}
          serverUuid={effectiveServer}
          onChanged={invalidate}
        />
      ))}

      {d.databases.map((db) => (
        <DatabaseCard
          key={db.id}
          workflowId={workflowId}
          db={db}
          serverUuid={effectiveServer}
          onChanged={invalidate}
        />
      ))}

      <StepLog steps={d.steps} />
    </div>
  );
}

// ── Site card ───────────────────────────────────────────────────────

function PublishedContentEditor({ id, value, documentRoot, editable, pending, onSave }: {
  id: string;
  value: PublishedMigrationContent | null;
  documentRoot: string | null;
  editable: boolean;
  pending: boolean;
  onSave: (choice: PublishedMigrationContent | null) => void;
}) {
  const [opened, setOpened] = useState(!!value);
  const [directory, setDirectory] = useState(value?.directory ?? 'dist');
  const [spaFallback, setSpaFallback] = useState(value?.spaFallback ?? false);
  const [followUpOwner, setFollowUpOwner] = useState(value?.followUpOwner ?? '');
  const [notes, setNotes] = useState(value?.notes ?? '');
  const [confirmed, setConfirmed] = useState(!!value);
  const validChoice = publishedMigrationContentSchema.safeParse({ directory, spaFallback, followUpOwner, notes, confirmStatic: confirmed }).success;
  if (!opened) return editable ? (
    <Button variant="secondary" onClick={() => setOpened(true)}>Use published static files</Button>
  ) : null;
  return (
    <div className="space-y-3 rounded-lg border border-slate-200 p-3 dark:border-slate-800">
      <p className="font-medium">{value ? 'Published static files selected' : 'Migrate published static files'}</p>
      <p className="text-xs text-slate-600 dark:text-slate-300">
        Serve an existing published folder with nginx. No Git access or build runs during this migration.
        Apache .htaccess files are excluded; review their rules before cutover. Reconnecting Git remains a separate task for the repository owner. The original source details are retained below.
      </p>
      <p className="text-xs text-slate-600 dark:text-slate-300">
        Enter a child folder, or <strong>.</strong> when the document root already contains the published site.
        Using the current root requires a plan probe with index.html and no source-project markers.
      </p>
      {documentRoot && <p className="break-all font-mono text-xs">Selected source: {documentRoot.replace(/\/$/, '')}{directory.trim() === '.' ? '' : `/${directory.trim()}`}</p>}
      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <Label htmlFor={`published-directory-${id}`}>Published folder within document root</Label>
          <Input id={`published-directory-${id}`} value={directory} maxLength={255} disabled={!editable || pending}
            onChange={(event) => setDirectory(event.target.value)} placeholder="dist" />
        </div>
        <div>
          <Label htmlFor={`published-owner-${id}`}>Git follow-up owner</Label>
          <Input id={`published-owner-${id}`} value={followUpOwner} maxLength={200} disabled={!editable || pending}
            onChange={(event) => setFollowUpOwner(event.target.value)} />
        </div>
      </div>
      <div>
        <Label htmlFor={`published-notes-${id}`}>Publication and follow-up notes</Label>
        <Input id={`published-notes-${id}`} value={notes} maxLength={1000} disabled={!editable || pending}
          onChange={(event) => setNotes(event.target.value)} placeholder="Why published files are suitable and how Git will be reconnected" />
      </div>
      <label className="flex items-center gap-2 text-xs">
        <input type="checkbox" checked={spaFallback} disabled={!editable || pending} onChange={(event) => setSpaFallback(event.target.checked)} />
        Serve index.html for client-side routes (SPA fallback)
      </label>
      <label className="flex items-center gap-2 text-xs">
        <input type="checkbox" checked={confirmed} disabled={!editable || pending} onChange={(event) => setConfirmed(event.target.checked)} />
        I confirm these are published static files and the website needs no server runtime or scheduled tasks.
      </label>
      {editable && (
        <div className="flex flex-wrap gap-2">
          <Button loading={pending} disabled={!validChoice}
            onClick={() => onSave({ directory: directory.trim(), spaFallback, followUpOwner: followUpOwner.trim(), notes: notes.trim(), confirmStatic: true })}>
            Save published-file migration
          </Button>
          <Button variant="secondary" disabled={pending} onClick={() => value ? onSave(null) : setOpened(false)}>
            {value ? 'Return to Git build' : 'Cancel'}
          </Button>
        </div>
      )}
      <p className="text-xs text-slate-600 dark:text-slate-300">Changing this selection resets site acceptance checks. After provisioning it is locked.</p>
    </div>
  );
}

function SiteCard({
  workflowId,
  site,
  serverUuid,
  onChanged,
}: {
  workflowId: string;
  site: MigrationSiteDto;
  serverUuid: string;
  onChanged: () => void;
}) {
  const base = `/api/migration/workflows/${workflowId}/sites/${site.id}`;
  const act = useMutation({
    mutationFn: (op: { path: string; body?: unknown }) => api.post(`${base}/${op.path}`, op.body),
    onSuccess: onChanged,
  });
  const check = useMutation({
    mutationFn: (body: { key: string; state: MigrationCheckState }) => api.patch(`${base}/checklist`, body),
    onSuccess: onChanged,
  });
  const edit = useMutation({
    mutationFn: (body: unknown) => api.patch(base, body),
    onSuccess: onChanged,
  });
  const [gitCredentialUuid, setGitCredentialUuid] = useState('');
  const [targetIpv4, setTargetIpv4] = useState('');
  const validTarget = cutoverMigrationSiteSchema.safeParse({ confirm: true, targetIpv4: targetIpv4.trim() || undefined }).success;

  const canProvision = site.state === 'pending';
  const canTransfer = site.state === 'provisioned' || site.state === 'content_moved';
  const canVerify = site.state === 'content_moved' || site.state === 'verified';
  const canCutover = site.state === 'verified';
  const canRevert = site.state === 'cutover';

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            {site.domain}
            <Badge tone={siteTone(site.state)}>{site.state.replace('_', ' ')}</Badge>
            <Badge tone="gray">{site.plan.publishedContent ? 'Published static files' : BUILD_PATH_LABELS[site.buildPath]}</Badge>
          </span>
        }
        description={site.plan.publishedContent ? 'Serve the selected published folder; original Git details are retained for reconnection.' : site.plan.reason}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {canProvision && (
              <Button
                loading={act.isPending}
                disabled={
                  !serverUuid
                  || site.buildPath === 'none'
                  || (
                    !site.plan.publishedContent && (site.plan.gitSource?.creationPath === 'github-app'
                      || site.plan.gitSource?.creationPath === 'deploy-key')
                    && !gitCredentialUuid.trim()
                  )
                  || (!site.plan.publishedContent && site.plan.gitSource?.creationPath === 'operator-review')
                }
                onClick={() =>
                  act.mutate({
                    path: 'provision',
                    body: {
                      serverUuid,
                      ...(!site.plan.publishedContent && site.plan.gitSource?.creationPath === 'github-app'
                        ? { githubAppUuid: gitCredentialUuid.trim() }
                        : {}),
                      ...(!site.plan.publishedContent && site.plan.gitSource?.creationPath === 'deploy-key'
                        ? { privateKeyUuid: gitCredentialUuid.trim() }
                        : {}),
                    },
                  })
                }
              >
                {site.buildPath === 'forwarding' ? 'Provision redirect' : 'Provision app'}
              </Button>
            )}
            {canTransfer && (
              <Button loading={act.isPending} onClick={() => act.mutate({ path: 'transfer' })}>
                {site.buildPath === 'forwarding'
                  ? 'Stage redirect'
                  : site.state === 'content_moved'
                    ? 'Re-sync content'
                    : 'Transfer content'}
              </Button>
            )}
            {canVerify && site.stagingFqdn && (
              <Button variant="secondary" loading={act.isPending} onClick={() => act.mutate({ path: 'verify' })}>
                Run staging checks
              </Button>
            )}
            {canCutover && (
              <div className="w-full space-y-1">
                <Label htmlFor={`cutover-ip-${site.id}`}>Destination public IPv4 (optional override)</Label>
                <Input id={`cutover-ip-${site.id}`} value={targetIpv4} placeholder="Use the server IPv4 address"
                  onChange={(event) => setTargetIpv4(event.target.value)} disabled={act.isPending} />
                <p className="text-xs text-slate-500">Enter the verified public address when the server uses an internal hostname. Web A records will use this address.</p>
              </div>
            )}
            {canCutover && (
              <Button
                variant="secondary"
                loading={act.isPending}
                disabled={!validTarget}
                onClick={() => {
                  if (window.confirm(`Cut ${site.domain} over to Coolify? This changes live DNS.`)) {
                    act.mutate({
                      path: 'cutover',
                      body: { confirm: true, manual: false, targetIpv4: targetIpv4.trim() || undefined },
                    });
                  }
                }}
              >
                Cut over DNS
              </Button>
            )}
            {canRevert && (
              <Button
                variant="danger"
                loading={act.isPending}
                onClick={() => act.mutate({ path: 'revert' })}
              >
                <Undo2 className="size-4" /> Revert DNS
              </Button>
            )}
          </div>
        }
      />
      <div className="space-y-3 px-5 py-4 text-sm">
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-slate-600 dark:text-slate-300">
          {site.stagingFqdn && (
            <span>
              Staging:{' '}
              <a
                href={site.stagingFqdn.startsWith('http') ? site.stagingFqdn : `https://${site.stagingFqdn}`}
                target="_blank"
                rel="noreferrer"
                className="text-brand-600 hover:underline"
              >
                {site.stagingFqdn}
              </a>
            </span>
          )}
          {site.plan.docroot && <span className="font-mono text-xs">{site.plan.docroot}</span>}
          <span>
            Zone: {site.plan.zone.panelManaged ? 'panel-managed' : 'external (manual cutover)'}
          </span>
          <span>Shape: {site.plan.destinationShape.replace('-', ' ')}</span>
        </div>
        <div className="rounded-lg bg-slate-50 p-3 text-xs dark:bg-slate-800/60">
          <div className="font-medium text-slate-700 dark:text-slate-200">
            Routed names ({site.plan.names.length})
          </div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {site.plan.names.map((name) => (
              <Badge key={`${name.kind}:${name.name}`} tone="gray">
                {name.name} · {name.kind}{name.canonicalRedirect ? ' · canonical redirect' : ''}
              </Badge>
            ))}
          </div>
        </div>
        {site.plan.gitSource && ['nodejs', 'static'].includes(site.plan.runtime?.kind) && (
          <PublishedContentEditor
            key={JSON.stringify(site.plan.publishedContent ?? null)}
            id={site.id}
            documentRoot={site.plan.docroot}
            value={site.plan.publishedContent ?? null}
            editable={canProvision && (site.plan.scheduledTasks?.length ?? 0) === 0 && !site.plan.redirect}
            pending={edit.isPending}
            onSave={(publishedContent) => edit.mutate({ publishedContent })}
          />
        )}
        <div className="grid gap-3 md:grid-cols-2">
          <div className="rounded-lg border border-slate-200 p-3 text-xs dark:border-slate-800">
            <div className="font-medium text-slate-700 dark:text-slate-200">
              Source evidence
            </div>
            {site.plan.gitSource ? (
              <div className="mt-1 space-y-1">
                <p className="break-all font-mono">{site.plan.gitSource.repositoryUrl}</p>
                <p>
                  Branch <strong>{site.plan.gitSource.branch}</strong> ·{' '}
                  {site.plan.gitSource.access} ·{' '}
                  {site.plan.gitSource.creationPath.replaceAll('-', ' ')}
                </p>
                <p>
                  Plesk deploy mode {site.plan.gitSource.deploymentMode}
                  {site.plan.gitSource.deploymentPath
                    ? ` · path ${site.plan.gitSource.deploymentPath}`
                    : ''}
                </p>
                {site.plan.gitSource.postDeployActions.configured && (
                  <p className="font-medium text-amber-700 dark:text-amber-400">
                    Post-deploy actions require review. Command bodies were not collected and
                    inventory never runs or changes them.
                  </p>
                )}
                {site.state === 'pending' && !site.plan.publishedContent && (
                  <div className="mt-2 space-y-2">
                    <Label htmlFor={`git-path-${site.id}`}>Coolify Git creation path</Label>
                    <Select
                      id={`git-path-${site.id}`}
                      value={site.plan.gitSource.creationPath}
                      onChange={(event) =>
                        edit.mutate({ gitCreationPath: event.target.value })
                      }
                    >
                      <option value="public">Public repository</option>
                      <option value="github-app">Private GitHub App</option>
                      <option value="deploy-key">Private deploy key</option>
                      <option value="operator-review">Operator review</option>
                    </Select>
                    {(site.plan.gitSource.creationPath === 'github-app'
                      || site.plan.gitSource.creationPath === 'deploy-key') && (
                      <div>
                        <Label htmlFor={`git-credential-${site.id}`}>
                          {site.plan.gitSource.creationPath === 'github-app'
                            ? 'Coolify GitHub App UUID'
                            : 'Coolify private-key UUID'}
                        </Label>
                        <Input
                          id={`git-credential-${site.id}`}
                          type="password"
                          autoComplete="off"
                          value={gitCredentialUuid}
                          onChange={(event) => setGitCredentialUuid(event.target.value)}
                          placeholder="Reference only; key material never enters CPM"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <p className="mt-1">
                Content source · {site.buildPath === 'php' ? 'generated PHP-FPM/nginx image' : 'generated destination image'}
              </p>
            )}
          </div>
          <div className="rounded-lg border border-slate-200 p-3 text-xs dark:border-slate-800">
            <div className="font-medium text-slate-700 dark:text-slate-200">
              {site.plan.publishedContent ? 'Original runtime and portable settings' : 'Runtime and portable settings'}
            </div>
            {site.plan.runtime && site.plan.healthCheck ? (
              <div className="mt-1 space-y-1">
                <p>
                  <strong>{site.plan.runtime.kind}</strong> · {site.plan.runtime.buildPack}
                  {site.plan.runtime.version
                    ? ` · ${site.plan.runtime.version}${site.plan.runtime.pinned ? ' pinned' : ''}`
                    : ''}
                </p>
                <p>
                  Health: {site.plan.healthCheck.enabled ? 'enabled' : 'review required'} ·{' '}
                  {site.plan.healthCheck.path} :{site.plan.healthCheck.port}
                </p>
                {site.plan.runtime.environmentReferences.length > 0 && (
                  <p>
                    Environment references: {site.plan.runtime.environmentReferences.join(', ')}
                  </p>
                )}
                {site.plan.runtime.configReferences.length > 0 && (
                  <p>Config references: {site.plan.runtime.configReferences.join(', ')}</p>
                )}
                {site.plan.runtime.startupReferences.length > 0 && (
                  <p>Startup references: {site.plan.runtime.startupReferences.join(', ')}</p>
                )}
                {(site.plan.scheduledTasks?.length ?? 0) > 0 && (
                  <p>
                    Scheduled tasks staged disabled: {site.plan.scheduledTasks.length}
                  </p>
                )}
              </div>
            ) : (
              <p className="mt-1 text-amber-700 dark:text-amber-400">
                Legacy plan schema — regenerate this plan before provisioning.
              </p>
            )}
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 p-3 text-xs dark:border-slate-800">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span>
              Certificate: <strong>{site.plan.certificate.kind}</strong>
              {site.plan.certificate.certificateName && ` · ${site.plan.certificate.certificateName}`}
              {site.plan.certificate.strategy && ` · ${site.plan.certificate.strategy.replaceAll('-', ' ')}`}
            </span>
            {site.state === 'pending' && site.plan.certificate.unattendedCutoverBlocked && (
              <span className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  className="px-2 py-1 text-xs"
                  loading={edit.isPending}
                  onClick={() => edit.mutate({ certificateStrategy: 'manual-export' })}
                >
                  Use export/install path
                </Button>
                <Button
                  variant="ghost"
                  className="px-2 py-1 text-xs"
                  loading={edit.isPending}
                  onClick={() => edit.mutate({ certificateStrategy: 'switch-to-lets-encrypt' })}
                >
                  Switch to Let&apos;s Encrypt
                </Button>
              </span>
            )}
          </div>
          {site.plan.certificate.unattendedCutoverBlocked && (
            <p className="mt-1 text-amber-700 dark:text-amber-400">
              Unattended cutover is blocked until an explicit certificate path is selected and verified.
            </p>
          )}
        </div>
        {(site.plan.protectedPaths.length > 0
          || (site.plan.hostingSettings?.reviewedReplacements.length ?? 0) > 0) && (
          <div className="rounded-lg border border-amber-200 p-3 text-xs dark:border-amber-900">
            {site.plan.protectedPaths.length > 0 && (
              <p>
                Protected paths: {site.plan.protectedPaths.map((path) => path.path).join(', ')}
                {' '}— recreate access rules; credentials were not captured.
              </p>
            )}
            {(site.plan.hostingSettings?.reviewedReplacements.length ?? 0) > 0 && (
              <ul className="mt-1 list-disc pl-5">
                {site.plan.hostingSettings!.reviewedReplacements.map((replacement) => (
                  <li key={replacement.sourceKey}>
                    {replacement.sourceKey}: {replacement.replacement}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {site.lastError && <ErrorNote error={site.lastError} />}
        {act.error && <ErrorNote error={act.error} />}
        {edit.error && <ErrorNote error={edit.error} />}

        {site.checklist.length > 0 && (
          <div>
            <div className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-400">
              Acceptance checklist
            </div>
            <ul className="space-y-1">
              {site.checklist.map((item) => (
                <li key={item.key} className="flex items-center justify-between gap-3">
                  <span className="text-slate-700 dark:text-slate-200">
                    {item.label}
                    {item.detail && <span className="ml-2 text-xs text-slate-400">{item.detail}</span>}
                  </span>
                  <span className="flex items-center gap-2">
                    <Badge tone={checkTone(item.state)}>{item.state}</Badge>
                    {item.kind === 'manual' && item.state !== 'passed' && (
                      <Button
                        variant="ghost"
                        className="px-2 py-1 text-xs"
                        loading={check.isPending}
                        onClick={() => check.mutate({ key: item.key, state: 'passed' })}
                      >
                        Mark passed
                      </Button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {site.cutover?.mode === 'manual' && site.cutover.manualInstructions && (
          <div className="rounded-lg bg-slate-50 p-3 text-xs dark:bg-slate-800/60">
            <div className="mb-1 font-medium">Point these records at the Coolify server:</div>
            <ul className="font-mono">
              {site.cutover.manualInstructions.map((r, i) => (
                <li key={i}>
                  {r.name} {r.type} → {r.content}
                  {r.previousValues && r.previousValues.length > 0 && (
                    <span className="ml-2 text-slate-500">
                      (rollback: {r.previousValues.map((value) => `${value.type} ${value.content} TTL ${value.ttl ?? 'zone'}`).join('; ')})
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Card>
  );
}

// ── Database card ───────────────────────────────────────────────────

function DatabaseCard({
  workflowId,
  db,
  serverUuid,
  onChanged,
}: {
  workflowId: string;
  db: MigrationDatabaseDto;
  serverUuid: string;
  onChanged: () => void;
}) {
  const base = `/api/migration/workflows/${workflowId}/databases/${db.id}`;
  const act = useMutation({
    mutationFn: (op: { path: string; body?: unknown }) => api.post(`${base}/${op.path}`, op.body),
    onSuccess: onChanged,
  });

  const canProvision = db.state === 'pending';
  const canRestore = db.state === 'provisioned' || db.state === 'restored';
  const canVerify = db.state === 'restored' || db.state === 'verified';

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            DB: {db.name}
            <Badge tone={siteTone(db.state)}>{db.state}</Badge>
            <Badge tone="gray">
              {db.engine}
              {db.targetEngine && ` → ${db.targetEngine}`}
            </Badge>
          </span>
        }
        description={
          db.targetEngine ? undefined : (
            <>
              Unsupported engine — migrate manually (see the{' '}
              <HelpLink topic="migration/overview">migration guide</HelpLink>).
            </>
          )
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {canProvision && db.targetEngine && (
              <Button
                loading={act.isPending}
                disabled={!serverUuid}
                onClick={() => act.mutate({ path: 'provision', body: { serverUuid } })}
              >
                Provision database
              </Button>
            )}
            {canRestore && (
              <Button loading={act.isPending} onClick={() => act.mutate({ path: 'restore' })}>
                Restore dump
              </Button>
            )}
            {canVerify && (
              <Button variant="secondary" loading={act.isPending} onClick={() => act.mutate({ path: 'verify' })}>
                Verify row counts
              </Button>
            )}
          </div>
        }
      />
      <div className="space-y-2 px-5 py-4 text-sm">
        {db.lastError && <ErrorNote error={db.lastError} />}
        {act.error && <ErrorNote error={act.error} />}
        {db.tableCounts && (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="text-slate-400">
                <tr>
                  <th className="py-1 pr-4 font-medium">Table</th>
                  <th className="py-1 pr-4 font-medium">Source</th>
                  <th className="py-1 pr-4 font-medium">Target</th>
                  <th className="py-1 font-medium">Match</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {db.tableCounts.map((t) => (
                  <tr key={t.table}>
                    <td className="py-0.5 pr-4">{t.table}</td>
                    <td className="py-0.5 pr-4">{t.source ?? '—'}</td>
                    <td className="py-0.5 pr-4">{t.target ?? '—'}</td>
                    <td className="py-0.5">
                      <Badge tone={t.match ? 'green' : 'red'}>{t.match ? 'ok' : 'diff'}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}

// ── Step log ────────────────────────────────────────────────────────

function StepLog({ steps }: { steps: MigrationWorkflowDetailDto['steps'] }) {
  if (steps.length === 0) return null;
  return (
    <Card>
      <CardHeader title="Recent steps" description="The last automated operations run for this migration." />
      <ul className="divide-y divide-slate-200 text-sm dark:divide-slate-800">
        {steps.map((s) => (
          <li key={s.id} className="px-5 py-2">
            <div className="flex items-center justify-between gap-3">
              <span className="font-mono text-xs">{s.kind}</span>
              <span className="flex items-center gap-2">
                <Badge tone={s.status === 'succeeded' ? 'green' : s.status === 'failed' ? 'red' : 'blue'}>
                  {s.status}
                </Badge>
                <span className="text-xs text-slate-400">{timeAgo(s.finishedAt ?? s.createdAt)}</span>
              </span>
            </div>
            {(s.output || s.error) && (
              <pre className="mt-1 max-h-24 overflow-auto whitespace-pre-wrap break-words text-xs text-slate-500">
                {s.error ?? s.output}
              </pre>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}
